import { Box } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { InlineEdit, TextButton } from "../ui/primitives";
import { v2 } from "../ui/v2Tokens";
import { MacroLine, TotalRow } from "../ui/MacroChips";
import FoodRow from "./FoodRow";
import FoodSearchField from "./FoodSearchField";
import PickSavedMealDialog from "../dialogs/PickSavedMealDialog";
import { listTotals, newId, type AltMealV2, type FoodItemV2 } from "../model/nutritionV2Model";

type Props = {
  mealName: string;
  mainItems: FoodItemV2[];
  alternatives: AltMealV2[];
  onChange: (alternatives: AltMealV2[]) => void;
  /**
   * Whether the list is unfolded, and whether the saved-meal picker is up.
   *
   * Both live in the MEAL CARD, because the control that drives them does: it sits on the meal's
   * total row (see `AlternativeMealsControls`), which this section cannot reach from here.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
};

/**
 * The control the meal's TOTAL row carries: how many alternatives there are, whether they are
 * unfolded, and the way to add one.
 *
 * It rides on the total rather than heading a band of its own because that band was three-quarters
 * empty — a count, a caption and a button — and it separated the foods from the figures they add
 * up to. The caption it used to carry ("אינן נספרות בסיכום היומי") is gone with it: sitting on the
 * total, "ארוחות חלופיות" beside a total that plainly excludes them says the same thing shorter,
 * and each alternative's own header already spells it out.
 */
export const AlternativeMealsControls = ({
  count,
  open,
  onToggle,
  onAdd,
}: {
  count: number;
  open: boolean;
  onToggle: () => void;
  onAdd: () => void;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flex: "none" }}>
    <Box
      component="button"
      type="button"
      // the label spells out the count, which the visible chip only shows — a screen
      // reader otherwise hears the number as a fragment adrift from the noun
      aria-label={`ארוחות חלופיות (${count})`}
      aria-expanded={open}
      onClick={onToggle}
      disabled={count === 0}
      sx={{
        border: "none",
        background: "transparent",
        fontFamily: "inherit",
        fontSize: "12.5px",
        fontWeight: 500,
        color: count ? v2.ink2 : v2.ink4,
        cursor: count ? "pointer" : "default",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: 0,
        whiteSpace: "nowrap",
        "&:hover": count ? { color: v2.ink } : {},
      }}
    >
      {count > 0 && (open ? <ExpandLess sx={{ fontSize: 15 }} /> : <ExpandMore sx={{ fontSize: 15 }} />)}
      ארוחות חלופיות
      <Box
        component="span"
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          background: "#E3E9EE",
          color: v2.ink2,
          padding: "1px 7px",
          borderRadius: "5px",
        }}
      >
        {count}
      </Box>
    </Box>
    <TextButton onClick={onAdd} sx={{ height: "26px", padding: "0 10px", fontSize: "12px" }}>
      + ארוחה חלופית
    </TextButton>
  </Box>
);

/**
 * Whole alternative meals ("ארוחה חלופית") — the trainee eats the main meal OR one of
 * these, so they are excluded from the daily totals.
 */
const AlternativeMealsSection = ({
  mealName,
  mainItems,
  alternatives,
  onChange,
  open,
  onOpenChange,
  pickerOpen,
  onPickerOpenChange,
}: Props) => {

  const updateAlt = (uid: string, changes: Partial<AltMealV2>) =>
    onChange(
      alternatives.map((a) =>
        a.uid === uid
          ? {
              ...a,
              ...changes,
              ...(changes.items ? { items: changes.items.map((item, i) => ({ ...item, order: i })) } : {}),
            }
          : a
      )
    );

  const moveAltItem = (altUid: string, itemUid: string, direction: -1 | 1) => {
    const alt = alternatives.find((a) => a.uid === altUid);
    if (!alt) return;
    const index = alt.items.findIndex((it) => it.uid === itemUid);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= alt.items.length) return;
    const next = [...alt.items];
    [next[index], next[target]] = [next[target], next[index]];
    updateAlt(altUid, { items: next });
  };

  const addAlternative = (result: { name: string; items: FoodItemV2[] }) => {
    onChange([...alternatives, { uid: newId("alt"), name: result.name, items: result.items }]);
    onOpenChange(true);
    onPickerOpenChange(false);
  };

  return (
    <Box
      sx={{
        borderTop: alternatives.length && open ? `1px solid ${v2.lineSoft}` : "none",
        background: alternatives.length && open ? "#F4F7F9" : "transparent",
      }}
    >
      {open && alternatives.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", padding: { xs: "10px 8px 12px", md: "12px 16px 14px" } }}>
          {alternatives.map((alt, ai) => {
            const totals = listTotals(alt.items);
            return (
              <Box
                key={alt.uid}
                data-testid="alternative-meal"
                sx={{ border: "1px dashed #CFD8E0", borderRadius: v2.r, background: v2.surface, overflow: "hidden" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    flexWrap: "wrap",
                    padding: { xs: "8px 10px", md: "10px 12px" },
                    borderBottom: `1px dashed ${v2.line}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", flex: 1 }}>
                    <Box
                      sx={{
                        fontSize: "10.5px",
                        fontWeight: 600,
                        padding: "2px 7px",
                        borderRadius: "5px",
                        background: v2.lineSoft,
                        color: v2.ink4,
                        whiteSpace: "nowrap",
                        flex: "none",
                      }}
                    >
                      ארוחה חלופית {ai + 1}
                    </Box>
                    <InlineEdit
                      size="sm"
                      ariaLabel={`שם ארוחה חלופית ${ai + 1}`}
                      value={alt.name}
                      onChange={(name) => updateAlt(alt.uid, { name })}
                      sx={{ minWidth: "110px", flex: { xs: "1 1 auto", md: "0 1 auto" } }}
                    />
                    {alt.items.length > 0 && <MacroLine totals={totals} size={11} />}
                  </Box>
                  <TextButton
                    tone="danger"
                    onClick={() => onChange(alternatives.filter((a) => a.uid !== alt.uid))}
                    quiet
                    sx={{ height: { xs: "36px", md: "26px" }, padding: "0 9px", fontSize: "11.5px" }}
                  >
                    מחק
                  </TextButton>
                </Box>

                {alt.items.map((item, i) => (
                  <FoodRow
                    key={item.uid}
                    item={item}
                    canMoveUp={i > 0}
                    canMoveDown={i < alt.items.length - 1}
                    onMove={(direction) => moveAltItem(alt.uid, item.uid, direction)}
                    onChange={(next) =>
                      updateAlt(alt.uid, { items: alt.items.map((it) => (it.uid === item.uid ? next : it)) })
                    }
                    onDelete={() => updateAlt(alt.uid, { items: alt.items.filter((it) => it.uid !== item.uid) })}
                  />
                ))}

                {alt.items.length > 0 && <TotalRow totals={totals} label="סה״כ חלופה:" />}

                <FoodSearchField
                  placeholder="חפש מוצר לארוחה החלופית..."
                  onAdd={(item) => updateAlt(alt.uid, { items: [...alt.items, { ...item, order: alt.items.length }] })}
                />
              </Box>
            );
          })}
        </Box>
      )}

      <PickSavedMealDialog
        open={pickerOpen}
        onClose={() => onPickerOpenChange(false)}
        title={`ארוחה חלופית ל${mealName}`}
        entityLabel="ארוחה חלופית"
        copyFrom={mainItems}
        allowEmpty
        /* "ארוחה חלופית 1", not "חלופה 1". A per-food חלופה is a different feature living one
           level down on the rows, and a trainer reading the two side by side had no way to tell
           from the name which of them they had just made. */
        defaultName={`ארוחה חלופית ${alternatives.length + 1}`}
        onConfirm={addAlternative}
      />
    </Box>
  );
};

export default AlternativeMealsSection;
